import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    FiArrowLeft,
    FiMapPin,
    FiArrowRight,
    FiCheck,
} from "react-icons/fi";

import "./RegionDetail.css";

// images
import samarkandImg from "../photos/guri_amir.webp";
import bukharaImg from "../photos/andijon.webp";
import khivaImg from "../photos/xiva1.webp";
import tashkentImg from "../photos/toshkent3.webp";
import shahrisabzImg from "../photos/uzb_shahr_oqsaroy1.webp";
import vodiyImg from "../photos/uzb_farg_xonsaroy1.webp";
import termizImg from "../photos/uzb_term_hakim2.webp";
import qoraqalpoqImg from "../photos/uzb_qq_ayozqala2.webp";

// DATA
import samarkand from "../data/region/samarqand";
import bukhara from "../data/region/buxoro";
import khiva from "../data/region/xiva";
import tashkent from "../data/region/toshkent";
import shahrisabz from "../data/region/shahxrisabz";
import vodiy from "../data/region/vodiy";
import termiz from "../data/region/termiz";
import qoraqalpoq from "../data/region/qoraqalpoq";

// IMAGE MAP
const images = {
    samarkand: samarkandImg,
    bukhara: bukharaImg,
    khiva: khivaImg,
    tashkent: tashkentImg,
    shahrisabz: shahrisabzImg,
    vodiy: vodiyImg,
    termiz: termizImg,
    qoraqalpoq: qoraqalpoqImg,
};

// DATA MAP
const regions = {
    samarkand,
    bukhara,
    khiva,
    tashkent,
    shahrisabz,
    vodiy,
    termiz,
    qoraqalpoq,
};

const RegionDetail = () => {
    const { city } = useParams();
    const navigate = useNavigate();
    const { i18n } = useTranslation();

    const key = city?.toLowerCase().trim();

    const currentLang = (i18n.language || "en").split("-")[0];

    const region = regions[key];

    const info =
        region?.[currentLang] ||
        region?.en ||
        region?.uz ||
        null;

    const imageUrl = images[key];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [city]);

    if (!info) {
        return (
            <main className="region-page">
                <div className="region-not-found">
                    <span>ADAM VOYAGE</span>

                    <h1>Region not found</h1>

                    <p>
                        Ushbu hudud uchun ma'lumot topilmadi.
                    </p>

                    <button
                        onClick={() => navigate("/uzbekistan")}
                    >
                        <FiArrowLeft />
                        Uzbekistan
                    </button>
                </div>
            </main>
        );
    }

    return (
        <main className="region-page">

            {/* HERO */}
            <section className="region-hero">

                <div
                    className="region-hero-image"
                    style={{
                        backgroundImage: `url(${imageUrl})`,
                    }}
                />

                <div className="region-hero-overlay" />

                <div className="region-hero-content">

                    <button
                        className="region-back"
                        onClick={() =>
                            navigate("/uzbekistan")
                        }
                    >
                        <FiArrowLeft />
                        Barcha hududlar
                    </button>

                    <span className="region-eyebrow">
                        ADAM VOYAGE · UZBEKISTAN
                    </span>

                    <h1>{info.title}</h1>

                    <p>{info.intro}</p>

                    <div className="region-location">
                        <FiMapPin />
                        <span>
                            Discover Uzbekistan
                        </span>
                    </div>

                </div>

                <div className="region-scroll">
                    <span>SCROLL TO EXPLORE</span>
                    <div />
                </div>

            </section>

            {/* CONTENT */}
            <section className="region-content">

                {/* HISTORY */}
                {info.history && (
                    <section className="region-history">

                        <div className="region-heading">
                            <span className="region-label">
                                HISTORY
                            </span>

                            <h2>
                                {info.title}
                                <br />
                                <span>tarixi</span>
                            </h2>
                        </div>

                        <div className="history-text">
                            <div className="history-line" />

                            <p>
                                {info.history}
                            </p>
                        </div>

                    </section>
                )}

                {/* SECTIONS */}
                {info.sections?.map((sec, index) => (
                    <section
                        key={index}
                        className="region-section"
                    >

                        <div className="section-top">

                            <div className="section-number">
                                {String(index + 1).padStart(
                                    2,
                                    "0"
                                )}
                            </div>

                            <div>
                                <span className="region-label">
                                    EXPLORE
                                </span>

                                <h2>
                                    {sec.title}
                                </h2>
                            </div>

                        </div>

                        <div className="section-body">

                            <div className="section-text">

                                {sec.content && (
                                    <p>
                                        {sec.content}
                                    </p>
                                )}

                                {sec.details && (
                                    <p>
                                        {sec.details}
                                    </p>
                                )}

                                {Array.isArray(
                                    sec.facts
                                ) && (
                                        <div className="facts-list">

                                            {sec.facts.map(
                                                (
                                                    fact,
                                                    i
                                                ) => (
                                                    <div
                                                        className="fact"
                                                        key={i}
                                                    >
                                                        <FiCheck />

                                                        <span>
                                                            {
                                                                fact
                                                            }
                                                        </span>
                                                    </div>
                                                )
                                            )}

                                        </div>
                                    )}

                                {!Array.isArray(
                                    sec.facts
                                ) &&
                                    sec.facts && (
                                        <p>
                                            {sec.facts}
                                        </p>
                                    )}

                            </div>

                            {/* IMAGES */}
                            {sec.images?.length > 0 && (
                                <div className="region-images">

                                    {sec.images.map(
                                        (img, i) => (
                                            <div
                                                className="region-image"
                                                key={i}
                                            >
                                                <img
                                                    src={img}
                                                    alt={
                                                        sec.title
                                                    }
                                                    loading="lazy"
                                                />

                                                <span>
                                                    {String(
                                                        i +
                                                        1
                                                    ).padStart(
                                                        2,
                                                        "0"
                                                    )}
                                                </span>
                                            </div>
                                        )
                                    )}

                                </div>
                            )}

                        </div>

                    </section>
                ))}

                {/* CTA */}
                <section className="region-cta">

                    <div className="cta-glow" />

                    <span>
                        ADAM VOYAGE
                    </span>

                    <h2>
                        O‘zbekistonni
                        <br />
                        <em>kashf etishga tayyormisiz?</em>
                    </h2>

                    <p>
                        Ushbu hududni shunchaki o‘qib
                        qolmang — uni o‘z ko‘zingiz bilan
                        kashf eting.
                    </p>

                    <button
                        onClick={() =>
                            navigate("/tours")
                        }
                    >
                        Tour Packages
                        <FiArrowRight />
                    </button>

                </section>

                {/* BACK */}
                <div className="region-bottom-back">

                    <button
                        onClick={() =>
                            navigate("/uzbekistan")
                        }
                    >
                        <FiArrowLeft />
                        Barcha hududlarga qaytish
                    </button>

                </div>

            </section>

        </main>
    );
};

export default RegionDetail;